import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

/**
 * Renders briefing/notes text with Markdown-like formatting.
 *
 * Supports:
 *  - **bold** and Telegram-style *bold* (single asterisk)
 *  - Bullet lists (- foo / * foo)
 *  - Auto-linking of URLs, e-mails and phone numbers
 *  - Preserved line breaks
 *  - GFM extensions (strikethrough, task lists, tables)
 *
 * Pre-processing converts Telegram's *bold* into Markdown's **bold** while
 * leaving asterisks that act as list bullets ("* item") untouched.
 */
const telegramToMarkdown = (raw) => {
    if (!raw) return '';
    // Convert *bold* -> **bold** when the asterisks bracket non-whitespace and
    // aren't acting as list bullets at line-start.
    // Match: *something*  (something has no leading/trailing whitespace and no asterisks)
    return raw.replace(/(^|[^*\w])\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*(?![*\w])/g, '$1**$2**');
};

const linkClass = 'text-primary underline decoration-primary/40 hover:decoration-primary break-words';

const MarkdownNote = ({ text, className = '' }) => {
    const processed = useMemo(() => telegramToMarkdown(text || ''), [text]);

    return (
        <div
            className={`max-w-none break-words text-sm leading-relaxed ${className}`}
            data-testid="markdown-note"
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                    a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" className={linkClass} />
                    ),
                    p: ({ node, ...props }) => <p className="my-1.5" {...props} />,
                    ul: ({ node, ...props }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5" {...props} />,
                    ol: ({ node, ...props }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5" {...props} />,
                    li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
                    code: ({ inline, ...props }) =>
                        inline ? (
                            <code className="px-1 py-0.5 rounded bg-muted text-[0.85em]" {...props} />
                        ) : (
                            <code className="block p-2 rounded-lg bg-muted text-xs overflow-x-auto" {...props} />
                        ),
                    blockquote: ({ node, ...props }) => (
                        <blockquote className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground my-1.5" {...props} />
                    ),
                    h1: ({ node, ...props }) => <h3 className="text-base font-semibold mt-2 mb-1" {...props} />,
                    h2: ({ node, ...props }) => <h3 className="text-base font-semibold mt-2 mb-1" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="text-sm font-semibold mt-2 mb-1" {...props} />,
                }}
            >
                {processed}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownNote;
